// 邮箱注册/登录 API（后端 ProxyServer OAuthController，/api/oauth/email/*）
import SparkMD5 from 'spark-md5';
import { multiLanguageHeader } from '@/utils/languageHeader';

const BASE = (import.meta.env.VITE_API_BASE as string) || '';

// 客户端共享密钥（与后端 BaseController.PRIVATE_KEY 一致，防篡改签名）
const PRIVATE_KEY = '6d767896a2e4b60d';
const APP_ID = 'fateuni';

/** 验证码类型：1=新注册（与后端 VerifyCodeTypeConstant.SEND_MSG_FOR_NEW_REGISTER 一致） */
export const VERIFY_CODE_TYPE_REGISTER = 1;

export function md5(input: string): string {
  return SparkMD5.hash(input);
}

/** sign = MD5(salt + PRIVATE_KEY)，与后端 checkSign 口径一致 */
export function buildSign(salt: string): string {
  return md5(salt + PRIVATE_KEY);
}

/** 表单 POST（application/x-www-form-urlencoded，与后端 @ApiParam 表单接收一致） */
async function postForm(url: string, params: Record<string, string>): Promise<any> {
  const body = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}${url}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...multiLanguageHeader() },
    credentials: 'include',
    body,
  });
  const json = await res.json().catch(() => null);
  // 成功：{data: {...}}；失败：{message, status}（无 data 字段）
  if (json && json.data !== undefined) return json.data;
  throw new Error(json?.message || json?.msg || `请求失败（HTTP ${res.status}）`);
}

/** 发送邮箱注册验证码 */
export async function sendEmailVerifyCode(email: string): Promise<void> {
  const type = String(VERIFY_CODE_TYPE_REGISTER);
  const sign = buildSign(email + type + APP_ID);
  await postForm('/api/oauth/email/verify-code', { email, type, appId: APP_ID, sign });
}

/** 邮箱注册（密码明文传输），成功返回 accessToken */
export async function registerByEmail(email: string, password: string, verifyCode: string): Promise<string> {
  const sign = buildSign(email + password + verifyCode + APP_ID);
  const data = await postForm('/api/oauth/email/register', {
    email, password, verifyCode, appId: APP_ID, sign,
  });
  return data.accessToken;
}

/** 邮箱登录（密码明文传输），成功返回 accessToken */
export async function loginByEmail(email: string, password: string): Promise<string> {
  const sign = buildSign(email + password + APP_ID);
  const data = await postForm('/api/oauth/email/login', {
    email, password, appId: APP_ID, sign,
  });
  return data.accessToken;
}

/**
 * 用邮箱 accessToken 调 /api/auth/loginByThird 完成实际登录。
 * 后端以 thirdType=kk + credential=accessToken 解析出账号并建立/复用 kkid，
 * 返回登录 token 并 setCookie(AUTH_COOKIE, 30天)。成功后返回该 token。
 */
export async function loginByThirdWithAccessToken(accessToken: string): Promise<string> {
  const thirdType = 'kk';
  const sign = buildSign(thirdType + accessToken + APP_ID);
  const data = await postForm('/api/auth/loginByThird', {
    thirdType, credential: accessToken, appId: APP_ID, sign,
  });
  return data.token;
}

export interface AuthUserInfo {
  kkid: number;
  nickname: string;
  avatar: string;
  signature: string;
  status: number;
  kknum: number;
}

/** 获取当前登录用户信息（依赖 AUTH_COOKIE 认证） */
export async function getUserInfo(): Promise<AuthUserInfo> {
  const data = await postForm('/api/auth/getUserInfo', { appId: APP_ID });
  return data.user as AuthUserInfo;
}
